import { useIntl } from 'react-intl-next';

import { messages } from '../messages.ts';
import { type Participant } from '../types.ts';

const MAX_NUMBER_OF_NAMES_TO_DISPLAY = 3;
const MAX_CHARACTERS = 64;

/**
 * Returns the largest possible subset of names that can be joined with ', ' separators
 * without exceeding the specified character limit, and the count of names excluded.
 *
 * @param names - The array of names to consider.
 * @param  maxChars - The maximum allowed character count for the joined string.
 * @returns An object containing:
 *   - namesThatFit: The names that fit within the character limit.
 *   - numberOfNamesThatDidNotFit: The number of names that could not be included.
 */
export function fitNamesWithinLimit(
  names: string[],
  maxChars: number
): { namesThatFit: string[]; numberOfNamesThatDidNotFit: number } {
  let totalLength = 0;
  const included: string[] = [];

  for (let i = 0; i < names.length; i++) {
    // Add 2 for ', ' if it's not the first name
    const nameLength = names[i].length + (i > 0 ? 2 : 0);
    if (totalLength + nameLength > maxChars) {
      break;
    }
    included.push(names[i]);
    totalLength += nameLength;
  }

  return {
    namesThatFit: included,
    numberOfNamesThatDidNotFit: names.length - included.length
  };
}

export const useDisplayParticipants = (
  participants?: Participant[],
  maximumNumberOfNamesToDisplay: number = MAX_NUMBER_OF_NAMES_TO_DISPLAY
) => {
  const { formatMessage } = useIntl();

  const displayNames = participants
    ?.map((participant: Participant) => {
      return participant.identity.displayName;
    })
    .filter((name) => name !== undefined);

  if (!displayNames) {
    return '';
  }

  const firstNParticipants = displayNames.slice(0, maximumNumberOfNamesToDisplay);

  const { namesThatFit, numberOfNamesThatDidNotFit } = fitNamesWithinLimit(
    firstNParticipants,
    MAX_CHARACTERS
  );

  const visibleNames = namesThatFit.join(', ');

  // this is a count of two things:
  // 1. we limit the total number of names to display. to account for this, we need to count everything over the maximumNumberOfNamesToDisplay
  // 2. we limit the number of characters to display. to account for this, we need the count the names that did not fit
  // the sum of these two numbers will be the total number of names that are not shown
  const numberOfAdditionalParticipants =
    Math.max(displayNames.length - maximumNumberOfNamesToDisplay, 0) +
    numberOfNamesThatDidNotFit;

  const numberOfAdditionalParticipantsString = formatMessage(
    messages['presence.nameplate.more'],
    {
      numberOfAdditionalParticipants
    }
  );

  return `${visibleNames}${
    numberOfAdditionalParticipants > 0
      ? `, + ${numberOfAdditionalParticipantsString}`
      : ''
  }`;
};
